import {MatchingSectionEtatBadge} from '@/backoffice/domains/PdfOrderImport/MatchSectionComponents';
import {
  Box,
  Collapse,
  HStack,
  Link,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import {LigneMatching} from 'api-types/ffconnect';
import {useTranslation} from 'react-i18next';
import {TableComparaisonMatching} from './TableComparaisonMatching';
import {etatDeLaLigne} from './panier-pdf-lignes';
import {phraseDeControle} from './panier-pdf-phrase';

/**
 * Import PDF : synthèse du matching d'une ligne de panier face à la ligne du
 * bon de commande dont elle provient. Rendue sous la ligne produit, dans
 * `footerBlock`, où elle peut cohabiter avec l'alerte de validation.
 *
 * Après un remplacement d'article, le badge d'état et la phrase cèdent la place
 * à un tag « Remplacé » ; le lien ouvre toujours la correspondance initiale.
 */
export const MatchingLignePanier: React.FC<{
  ligneMatching: LigneMatching;
  estRemplacee?: boolean;
}> = ({ligneMatching, estRemplacee = false}) => {
  const {t, i18n} = useTranslation();
  const {isOpen, onToggle} = useDisclosure();
  // État de synthèse de la ligne, celui que porte le badge.
  const etat = etatDeLaLigne(ligneMatching);

  return (
    // Barre de synthèse et table dépliée forment un seul bloc : le radius est
    // porté ici, avec `overflow='hidden'` pour que la table adopte ses coins.
    <VStack
      align='stretch'
      spacing='0'
      mt='2'
      bg='gray.50'
      borderRadius='8px'
      overflow='hidden'
    >
      <HStack
        w='100%'
        px='3'
        py='1'
        color='gray.dark'
        bg='gray.light_bg'
      >
        {estRemplacee ? (
          <>
            <Box
              px={2}
              py={1}
              borderRadius='md'
              fontSize='xs'
              fontWeight='bold'
              bg='blue.main_light'
              color='blue.main'
              textAlign='center'
              flexShrink={0}
            >
              {t('panier.matching_remplace')}
            </Box>
            {/* Place restante pour garder « Correspondance initiale » à droite. */}
            <Box flex='1' />
          </>
        ) : (
          <>
            <MatchingSectionEtatBadge etat={etat} />
            {/* La phrase occupe la place restante et passe à la ligne plutôt que
            de repousser « Détails » hors de la barre. */}
            <Text
              fontSize='xs'
              flex='1'
            >
              {phraseDeControle(t, ligneMatching.matching, i18n.language)}
            </Text>
          </>
        )}
        <Link
          onClick={onToggle}
          fontSize='xs'
          textDecoration='underline'
          flexShrink={0}
        >
          {estRemplacee
            ? t('panier.matching_correspondance_initiale')
            : t('panier.matching_details')}
        </Link>
      </HStack>
      <Collapse
        in={isOpen}
        animateOpacity
      >
        {/* Tableau du matching */}
        <TableComparaisonMatching ligneMatching={ligneMatching} />
      </Collapse>
    </VStack>
  );
};
